"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import SubscribeButton from '@/components/SubscribeButton';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function useCountUp(target: number, duration: number, active: boolean) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setValue(target); clearInterval(timer); }
      else setValue(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return value;
}

const researchAreas = [
  {
    title: 'Dividend Stocks',
    tag: 'EQUITIES',
    desc: 'Proprietary screening of 4,200+ dividend-paying equities across 38 exchanges. Yield sustainability scoring, payout ratio analysis, and ex-dividend calendar intelligence.',
    img: '/assets/images/dividendchase_charts.png',
    alt: 'Glowing 3D dividend yield curves and equity market data on dark navy background',
  },
  {
    title: 'Forex Market Research',
    tag: 'FOREX',
    desc: 'Macro-driven currency pair analysis across G10 and EM markets. Algorithmic signal generation with institutional-grade risk-adjusted positioning models.',
    img: '/assets/images/dividendchase_forex.png',
    alt: 'Global forex market map with currency trading routes and exchange rate data',
  },
  {
    title: 'Digital Assets',
    tag: 'DIGITAL ASSETS',
    desc: 'On-chain analytics, DeFi protocol intelligence, and institutional-grade crypto market structure research covering 200+ assets with real-time sentiment scoring.',
    img: '/assets/images/dividendchase_crypto.png',
    alt: 'Holographic blockchain network nodes and cryptocurrency market data visualization',
  },
];

const pricingTiers = [
  {
    name: 'Silver',
    price: '$199',
    period: '/mo',
    desc: 'Essential research access for independent investors building dividend-focused portfolios.',
    features: [
      'Weekly dividend equity screener',
      'Forex macro briefings (1x/week)',
      'Digital asset sentiment dashboard',
      'Email research digests',
      'Basic watchlist (up to 25 equities)',
    ],
    cta: 'Subscribe Now',
    highlight: false,
    glowColor: null,
  },
  {
    name: 'Gold',
    price: '$499',
    period: '/mo',
    desc: 'Advanced intelligence for active portfolio managers requiring real-time signal access.',
    features: [
      'Everything in Silver',
      'Real-time algorithmic signal feed',
      'Proprietary dividend sustainability model',
      'Forex pair deep-dives (3x/week)',
      'Priority analyst desk access',
      'Custom watchlist (up to 100 equities)',
    ],
    cta: 'Subscribe Now',
    highlight: false,
    glowColor: null,
  },
  {
    name: 'Platinum',
    price: '$999',
    period: '/mo',
    desc: 'Full-spectrum research suite for family offices and high-net-worth mandates.',
    features: [
      'Everything in Gold',
      'On-chain metrics & DeFi analytics',
      'Catalyst calendar with earnings alerts',
      'Model portfolio access (3 strategies)',
      'Quarterly strategy calls',
      'White-label data exports',
    ],
    cta: 'Subscribe Now',
    highlight: false,
    glowColor: null,
  },
  {
    name: 'Diamond',
    price: 'Custom',
    period: '',
    desc: 'Bespoke institutional coverage for hedge funds, family offices, and institutional desks.',
    features: [
      'Everything in Platinum',
      'Dedicated research analyst',
      'White-label data API access',
      'Custom sector deep-dives',
      'Direct portfolio integration',
      'Unlimited model portfolio access',
    ],
    cta: 'Subscribe Now',
    highlight: true,
    glowColor: '#4A90E2',
  },
];

const stats = [
  { value: 4200, suffix: '+', label: 'Equities Covered' },
  { value: 38, suffix: '', label: 'Global Exchanges' },
  { value: 200, suffix: '+', label: 'Digital Assets' },
  { value: 97, suffix: '%', label: 'Signal Accuracy (12mo)' },
];

const terminalRows = [
  { symbol: 'JNJ', name: 'Johnson & Johnson', yield: '3.24%', score: '9.1', signal: 'BUY', positive: true },
  { symbol: 'KO', name: 'Coca-Cola Co.', yield: '3.08%', score: '8.7', signal: 'HOLD', positive: null },
  { symbol: 'PG', name: 'Procter & Gamble', yield: '2.41%', score: '9.4', signal: 'BUY', positive: true },
  { symbol: 'VZ', name: 'Verizon Communications', yield: '6.87%', score: '6.2', signal: 'WATCH', positive: null },
  { symbol: 'XOM', name: 'ExxonMobil Corp.', yield: '3.51%', score: '8.0', signal: 'BUY', positive: true },
  { symbol: 'T', name: 'AT&T Inc.', yield: '5.92%', score: '5.8', signal: 'REDUCE', positive: false },
];

export default function Homepage() {
  useReveal();

  const statsRef = useRef<HTMLDivElement>(null);
  const [statsActive, setStatsActive] = useState(false);

  const s0 = useCountUp(stats[0].value, 1800, statsActive);
  const s1 = useCountUp(stats[1].value, 1800, statsActive);
  const s2 = useCountUp(stats[2].value, 1800, statsActive);
  const s3 = useCountUp(stats[3].value, 1800, statsActive);
  const statValues = [s0, s1, s2, s3];

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsActive(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main style={{ background: '#0A2540', overflowX: 'hidden' }}>

        {/* ── HERO ── */}
        <section
          className="relative min-h-screen flex flex-col justify-center overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0A2540 0%, #0F4C81 100%)',
            paddingTop: '120px',
            paddingBottom: '80px',
          }}
        >
          {/* Hero background image */}
          <div className="absolute inset-0 z-0">
            <AppImage
              src="/assets/images/dividendchase_hero.png"
              alt="Cinematic institutional trading terminal with glowing cerulean blue financial data streams"
              fill
              className="object-cover"
              priority
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(135deg, rgba(10,37,64,0.93) 0%, rgba(10,37,64,0.72) 50%, rgba(15,76,129,0.88) 100%)' }}
            />
          </div>

          {/* Ambient glow orbs */}
          <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] rounded-full pointer-events-none z-0"
            style={{ background: 'radial-gradient(circle, rgba(74,144,226,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }} />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full pointer-events-none z-0"
            style={{ background: 'radial-gradient(circle, rgba(163,212,255,0.06) 0%, transparent 70%)', filter: 'blur(40px)' }} />

          <div className="relative z-10 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto', width: '100%' }}>
            <div className="max-w-4xl">
              <div className="reveal mb-5 flex items-center gap-3">
                <span
                  className="text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ color: '#A3D4FF', background: 'rgba(163,212,255,0.1)', border: '1px solid rgba(163,212,255,0.2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}
                >
                  Institutional Research Platform
                </span>
              </div>

              <h1
                className="fluid-h1 font-display font-extrabold leading-none tracking-tight text-white mb-8 reveal reveal-delay-1"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Institutional-Grade Insights<br />
                <span style={{ color: '#A3D4FF' }}>for Dividend Dominance.</span>
              </h1>

              <p
                className="fluid-body leading-relaxed mb-10 reveal reveal-delay-2"
                style={{ color: '#E3F2FD', opacity: 0.75, maxWidth: '580px', fontFamily: 'var(--font-body)' }}
              >
                Precision data models and independent research for high-net-worth investors. Covering dividend equities, global forex, and digital assets — exclusively for qualified capital.
              </p>

              <div className="flex flex-wrap gap-4 reveal reveal-delay-3">
                <Link href="#pricing" className="btn-primary">
                  Select Subscription
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
                <Link href="/research" className="btn-outline">
                  View Intelligence Feed
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 mt-12 reveal reveal-delay-4">
                {['SOC 2 Certified', 'Institutional Grade', 'Real-Time Data', 'Qualified Investors Only'].map((badge) => (
                  <div
                    key={badge}
                    className="flex items-center gap-2 text-xs font-mono"
                    style={{ color: '#E3F2FD', opacity: 0.5, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#A3D4FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {badge}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Floating live signal card */}
          <div
            className="absolute bottom-12 right-8 md:right-16 hidden lg:block reveal reveal-delay-3"
            style={{
              background: 'rgba(26,42,68,0.92)',
              border: '1px solid rgba(163,212,255,0.2)',
              borderRadius: '16px',
              padding: '20px 24px',
              backdropFilter: 'blur(20px)',
              minWidth: '220px',
            }}
          >
            <div className="text-xs font-mono mb-3" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.15em' }}>LIVE SIGNAL</div>
            <div className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>+12.4%</div>
            <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.5 }}>Avg. dividend yield alpha (YTD)</div>
            <div className="mt-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 inline-block" style={{ boxShadow: '0 0 6px #4ade80' }} />
              <span className="text-xs font-mono" style={{ color: '#4ade80', fontFamily: 'var(--font-mono)' }}>Models Active</span>
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <div ref={statsRef} style={{ background: '#0F4C81', borderTop: '1px solid rgba(163,212,255,0.15)', borderBottom: '1px solid rgba(163,212,255,0.15)' }}>
          <div className="px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            {stats.map((stat, i) => (
              <div key={stat.label} className={`reveal reveal-delay-${i + 1} text-center md:text-left`}>
                <div className="text-4xl md:text-5xl font-display font-extrabold mb-2"
                  style={{ fontFamily: 'var(--font-display)', color: '#A3D4FF', fontFeatureSettings: '"tnum"' }}>
                  {statValues[i]}{stat.suffix}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest"
                  style={{ color: '#E3F2FD', opacity: 0.55, fontFamily: 'var(--font-mono)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CORE RESEARCH AREAS (3-column grid) ── */}
        <section className="px-6 md:px-12 py-24" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div className="reveal">
              <span className="text-xs font-mono uppercase tracking-widest mb-4 block"
                style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
                Research Coverage
              </span>
              <h2 className="fluid-h2 font-display font-extrabold leading-none text-white" style={{ fontFamily: 'var(--font-display)' }}>
                Three asset classes.<br />
                <span style={{ color: '#A3D4FF' }}>One unified intelligence layer.</span>
              </h2>
            </div>
            <div className="reveal reveal-delay-1">
              <Link href="/research" className="btn-outline text-sm">
                Full Intelligence Feed
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </div>

          {/* 3-column CSS grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {researchAreas.map((area, i) => (
              <div
                key={area.title}
                className={`reveal reveal-delay-${i + 1} relative overflow-hidden group cursor-pointer flex flex-col`}
                style={{
                  background: '#1A2A44',
                  border: '1px solid #0F4C81',
                  borderRadius: '16px',
                  transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 20px 48px rgba(74,144,226,0.18)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                }}
              >
                {/* Square thumbnail placeholder */}
                <div className="relative w-full overflow-hidden" style={{ aspectRatio: '1/1', maxHeight: '220px' }}>
                  <AppImage
                    src={area.img}
                    alt={area.alt}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,37,64,0.2) 0%, rgba(26,42,68,0.7) 100%)' }} />
                  <div className="absolute top-4 left-4">
                    <span className="text-xs font-mono px-3 py-1 rounded-full"
                      style={{ background: 'rgba(163,212,255,0.12)', border: '1px solid rgba(163,212,255,0.25)', color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em' }}>
                      {area.tag}
                    </span>
                  </div>
                </div>

                <div className="p-7 flex flex-col flex-1">
                  <h3 className="text-xl font-display font-bold text-white mb-3" style={{ fontFamily: 'var(--font-display)' }}>
                    {area.title}
                  </h3>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: '#E3F2FD', opacity: 0.65 }}>
                    {area.desc}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-sm font-semibold" style={{ color: '#4A90E2' }}>
                    View Intelligence
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TERMINAL MOCKUP SECTION ── */}
        <section
          className="py-24 px-6 md:px-12 overflow-hidden"
          style={{ background: '#1A2A44', borderTop: '1px solid #1E3A5F', borderBottom: '1px solid #1E3A5F' }}
        >
          <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="text-center mb-14 reveal">
              <span className="text-xs font-mono uppercase tracking-widest mb-4 block"
                style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
                The DividendChase Terminal
              </span>
              <h2 className="fluid-h2 font-display font-extrabold leading-none text-white mb-5" style={{ fontFamily: 'var(--font-display)' }}>
                Institutional-grade data.<br />
                <span style={{ color: '#A3D4FF' }}>At your command.</span>
              </h2>
              <p className="text-base leading-relaxed mx-auto" style={{ color: '#E3F2FD', opacity: 0.6, maxWidth: '520px', fontFamily: 'var(--font-body)' }}>
                The DividendChase Terminal delivers algorithmic setups, macro-driven outlooks, and on-chain metrics in a single unified dashboard — purpose-built for institutional mandates.
              </p>
            </div>

            {/* Terminal UI mockup */}
            <div
              className="reveal reveal-delay-1 relative mx-auto"
              style={{
                background: '#0A2540',
                border: '1px solid rgba(163,212,255,0.2)',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 0 80px rgba(74,144,226,0.15), 0 40px 80px rgba(0,0,0,0.5)',
                maxWidth: '1100px',
              }}
            >
              {/* Terminal top bar */}
              <div className="flex items-center justify-between px-6 py-4" style={{ background: '#061829', borderBottom: '1px solid #1E3A5F' }}>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: '#f87171' }} />
                    <span className="w-3 h-3 rounded-full" style={{ background: '#fbbf24' }} />
                    <span className="w-3 h-3 rounded-full" style={{ background: '#4ade80' }} />
                  </div>
                  <span className="text-xs font-mono ml-4" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>
                    DIVIDENDCHASE TERMINAL v4.2 — DIVIDEND EQUITY SCREENER
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400" style={{ boxShadow: '0 0 6px #4ade80' }} />
                  <span className="text-xs font-mono" style={{ color: '#4ade80', fontFamily: 'var(--font-mono)' }}>LIVE</span>
                </div>
              </div>

              {/* Terminal body */}
              <div className="grid grid-cols-1 lg:grid-cols-4">
                {/* Left sidebar */}
                <div className="lg:col-span-1 p-5" style={{ borderRight: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.6 }}>
                    Filters
                  </div>
                  {[
                    { label: 'Yield Range', value: '2.5% – 8.0%' },
                    { label: 'Payout Ratio', value: '< 75%' },
                    { label: 'Sustainability Score', value: '≥ 7.0' },
                    { label: 'Market Cap', value: '$5B+' },
                    { label: 'Sector', value: 'All Sectors' },
                  ].map((f) => (
                    <div key={f.label} className="mb-4">
                      <div className="text-xs mb-1" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>{f.label}</div>
                      <div className="text-sm font-semibold" style={{ color: '#E3F2FD', fontFamily: 'var(--font-mono)' }}>{f.value}</div>
                    </div>
                  ))}

                  <div className="mt-6 pt-6" style={{ borderTop: '1px solid #1E3A5F' }}>
                    <div className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.6 }}>
                      Modules
                    </div>
                    {['Dividend Screener', 'Forex Signals', 'On-Chain Metrics', 'Catalyst Calendar', 'Model Portfolios'].map((m, idx) => (
                      <div key={m} className="flex items-center gap-2 mb-3 text-xs font-mono"
                        style={{ color: idx === 0 ? '#A3D4FF' : '#E3F2FD', opacity: idx === 0 ? 1 : 0.45, fontFamily: 'var(--font-mono)', cursor: 'pointer' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: idx === 0 ? '#A3D4FF' : '#1E3A5F' }} />
                        {m}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main data area */}
                <div className="lg:col-span-3 p-5">
                  {/* Mini charts row */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                      { label: 'Avg. Yield', value: '3.84%', delta: '+0.12%', positive: true },
                      { label: 'Signals Active', value: '47', delta: '+3 today', positive: true },
                      { label: 'Payout Coverage', value: '1.42x', delta: '-0.03x', positive: false },
                    ].map((metric) => (
                      <div key={metric.label} className="p-4 rounded-xl"
                        style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                        <div className="text-xs font-mono mb-2" style={{ color: '#E3F2FD', opacity: 0.45, fontFamily: 'var(--font-mono)' }}>{metric.label}</div>
                        <div className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)', fontFeatureSettings: '"tnum"' }}>{metric.value}</div>
                        <div className="text-xs font-mono" style={{ color: metric.positive ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>{metric.delta}</div>
                      </div>
                    ))}
                  </div>

                  {/* Data table */}
                  <div style={{ border: '1px solid #1E3A5F', borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="grid text-xs font-mono px-5 py-3"
                      style={{ gridTemplateColumns: '80px 1fr 100px 100px 100px', background: '#061829', color: '#E3F2FD', opacity: 0.45, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>
                      <span>TICKER</span>
                      <span>COMPANY</span>
                      <span className="text-right">YIELD</span>
                      <span className="text-right">SCORE</span>
                      <span className="text-right">SIGNAL</span>
                    </div>
                    {terminalRows.map((row, i) => (
                      <div
                        key={row.symbol}
                        className="grid items-center px-5 py-3 text-sm transition-colors"
                        style={{
                          gridTemplateColumns: '80px 1fr 100px 100px 100px',
                          borderTop: '1px solid #1E3A5F',
                          background: i % 2 === 0 ? 'transparent' : 'rgba(26,42,68,0.3)',
                          fontFamily: 'var(--font-mono)',
                          fontFeatureSettings: '"tnum"',
                        }}
                      >
                        <span className="font-bold" style={{ color: '#A3D4FF' }}>{row.symbol}</span>
                        <span className="text-xs" style={{ color: '#E3F2FD', opacity: 0.65 }}>{row.name}</span>
                        <span className="text-right font-semibold text-white">{row.yield}</span>
                        <span className="text-right" style={{ color: '#E3F2FD', opacity: 0.8 }}>{row.score}</span>
                        <span className="text-right">
                          <span className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              background: row.positive === true ? 'rgba(74,222,128,0.12)' : row.positive === false ? 'rgba(248,113,113,0.12)' : 'rgba(163,212,255,0.1)',
                              color: row.positive === true ? '#4ade80' : row.positive === false ? '#f87171' : '#A3D4FF',
                              border: `1px solid ${row.positive === true ? 'rgba(74,222,128,0.25)' : row.positive === false ? 'rgba(248,113,113,0.25)' : 'rgba(163,212,255,0.2)'}`,
                            }}>
                            {row.signal}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.3, fontFamily: 'var(--font-mono)' }}>
                      Showing 6 of 4,200+ equities · Updated 2 min ago
                    </span>
                    <Link href="/dashboard" className="text-xs font-semibold flex items-center gap-1" style={{ color: '#4A90E2' }}>
                      Access Full Terminal
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING / SUBSCRIPTION TIERS ── */}
        <section id="pricing" className="px-6 md:px-12 py-24" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="text-center mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest mb-4 block"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
              Subscription Tiers
            </span>
            <h2 className="fluid-h2 font-display font-extrabold leading-none text-white mb-5" style={{ fontFamily: 'var(--font-display)' }}>
              Access calibrated to<br />
              <span style={{ color: '#A3D4FF' }}>your mandate.</span>
            </h2>
            <p className="text-base leading-relaxed mx-auto" style={{ color: '#E3F2FD', opacity: 0.6, maxWidth: '480px', fontFamily: 'var(--font-body)' }}>
              Institutional research subscriptions structured for independent investors, active portfolio managers, and institutional desks.
            </p>
          </div>

          {/* 4-column pricing table */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {pricingTiers.map((tier, i) => (
              <div
                key={tier.name}
                className={`reveal reveal-delay-${i + 1} flex flex-col relative`}
                style={{
                  background: tier.highlight ? 'linear-gradient(160deg, #0F4C81 0%, #1A2A44 100%)' : '#1A2A44',
                  border: tier.highlight ? '1px solid rgba(74,144,226,0.6)' : '1px solid #1E3A5F',
                  borderRadius: '20px',
                  padding: '32px 28px',
                  boxShadow: tier.highlight ? '0 0 40px rgba(74,144,226,0.2), 0 0 80px rgba(74,144,226,0.08)' : 'none',
                }}
              >
                {tier.highlight && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-mono font-bold whitespace-nowrap"
                    style={{ background: '#4A90E2', color: '#FFFFFF', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em' }}
                  >
                    PREMIUM TIER
                  </div>
                )}

                <div className="mb-6">
                  <div className="text-xs font-mono uppercase tracking-widest mb-3"
                    style={{ color: tier.highlight ? '#A3D4FF' : '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                    {tier.name}
                  </div>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="text-4xl font-display font-extrabold text-white" style={{ fontFamily: 'var(--font-display)', fontFeatureSettings: '"tnum"' }}>
                      {tier.price}
                    </span>
                    {tier.period && (
                      <span className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>{tier.period}</span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.6 }}>{tier.desc}</p>
                </div>

                <div className="h-px mb-6" style={{ background: 'rgba(163,212,255,0.1)' }} />

                <ul className="space-y-3 flex-1 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm" style={{ color: '#E3F2FD', opacity: 0.75 }}>
                      <svg className="flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7l3.5 3.5L12 3" stroke="#A3D4FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <SubscribeButton tier={tier} />
              </div>
            ))}
          </div>
        </section>

        {/* ── INTELLIGENCE CTA ── */}
        <section
          className="relative py-28 px-6 md:px-12 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0F4C81 0%, #0A2540 60%, #061829 100%)' }}
        >
          <div className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(rgba(163,212,255,0.04) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(74,144,226,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />

          <div className="relative z-10 text-center reveal" style={{ maxWidth: '680px', margin: '0 auto' }}>
            <span className="text-xs font-mono uppercase tracking-widest mb-6 block"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
              Exclusive Access
            </span>
            <h2 className="fluid-h2 font-display font-extrabold leading-none text-white mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              Intelligence reserved for<br />
              <span style={{ color: '#A3D4FF' }}>serious capital.</span>
            </h2>
            <p className="text-lg leading-relaxed mb-10" style={{ color: '#E3F2FD', opacity: 0.65, fontFamily: 'var(--font-body)' }}>
              DividendChase memberships are available exclusively to qualified investors. Submit your application to begin the onboarding review process.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="#pricing" className="btn-primary">
                Select Subscription
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link href="/research" className="btn-ghost">
                View Research Intelligence
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}